import React, { useId, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { IconChevronDown, IconCheck } from '@tabler/icons-react';
import {
    SUPPORTED_LANGUAGES,
    DEFAULT_INTERVIEW_LANGUAGE,
    getLanguageShortCode,
    type SupportedLanguage,
} from '../lib/languages';
import { LanguageFlag } from './LanguageFlag';

interface LanguageSelectorProps {
    value: string;
    onChange: (code: string) => void;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    className?: string;
    /** When true, shows a "None" option that maps to an empty string value. */
    allowEmpty?: boolean;
    /** Placeholder text shown when allowEmpty is true and value is empty. */
    emptyLabel?: string;
    /**
     * Visual style of the trigger.
     * - `default`: full-width labelled control for forms.
     * - `compact`: a small flag + short-code pill (e.g. next to a Start button);
     *   the block label and helper text are omitted and `label` is used as the
     *   trigger's accessible name instead.
     */
    variant?: 'default' | 'compact';
}

/**
 * Language selector dropdown component
 * Displays supported Gemini languages with search/filter functionality
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
    value,
    onChange,
    label = 'Language',
    helperText,
    disabled = false,
    className = '',
    allowEmpty = false,
    emptyLabel = 'None',
    variant = 'default',
}) => {
    const controlId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [openUpwards, setOpenUpwards] = useState(false);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isCompact = variant === 'compact';

    // Get selected language info (null when allowEmpty and value is empty)
    const isEmptySelected = allowEmpty && !value;
    const selectedLanguage = isEmptySelected
        ? null
        : (SUPPORTED_LANGUAGES.find(l => l.code === value) ||
            SUPPORTED_LANGUAGES.find(l => l.code === DEFAULT_INTERVIEW_LANGUAGE));

    // Filter languages based on search
    const filteredLanguages = SUPPORTED_LANGUAGES.filter(lang =>
        lang.name.toLowerCase().includes(search.toLowerCase()) ||
        lang.nativeName.toLowerCase().includes(search.toLowerCase()) ||
        lang.code.toLowerCase().includes(search.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus the search box on open ONLY on fine-pointer (mouse) devices.
    //
    // On touch devices, auto-focusing pops the on-screen keyboard the instant
    // the panel opens. On iPhone that fights Safari's dynamic toolbar/keyboard
    // and made the page jump and stretch. The user expects the keyboard to stay
    // closed until they deliberately tap the search box, so we only steal focus
    // when a fine pointer is present (desktop). matchMedia is feature-detected
    // because it is absent in some test/SSR environments.
    useEffect(() => {
        if (!isOpen) return;
        const hasFinePointer =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: fine)').matches;
        if (hasFinePointer) searchInputRef.current?.focus();
    }, [isOpen]);

    // Size and orient the panel against the *visible* viewport.
    //
    // The panel is absolutely positioned, so when it runs past the fold it grows
    // the body-scrollable page instead of overlaying. window.innerHeight reports
    // the larger layout viewport (it ignores iOS Safari's dynamic toolbar), so
    // trusting it let the panel open downward off-screen and stretch the page on
    // iPhone. visualViewport gives the area the user can actually see. We open
    // toward whichever side has more room and cap the height to that room so the
    // panel always fits and scrolls internally. Runs in a layout effect so the
    // cap is applied before paint, avoiding a one-frame flash of a full-height
    // panel (which would momentarily stretch the page on open).
    useLayoutEffect(() => {
        if (!isOpen || !dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const GAP = 8; // matches the mt-2 / mb-2 offset between trigger and panel
        const EDGE = 16; // keep the panel clear of the screen edge
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const upwards = spaceAbove > spaceBelow;
        const available = (upwards ? spaceAbove : spaceBelow) - GAP - EDGE;
        setOpenUpwards(upwards);
        // Clamp to a usable range. The lower bound keeps the panel readable on
        // tight screens; in practice the larger side always exceeds it when the
        // trigger sits anywhere near the middle of a real device.
        setMaxHeight(Math.min(360, Math.max(160, available)));
    }, [isOpen]);

    const handleSelect = (lang: SupportedLanguage) => {
        onChange(lang.code);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className={className}>
            {label && !isCompact && (
                <label htmlFor={controlId} className="block text-sm font-medium text-muted-foreground mb-2">
                    {label}
                </label>
            )}

            <div className="relative" ref={dropdownRef}>
                {isCompact ? (
                    <button
                        id={controlId}
                        type="button"
                        aria-label={label}
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        disabled={disabled}
                        className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-muted-foreground transition-colors ${disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:text-foreground hover:bg-surface-hover/60 focus:outline-none focus:ring-1 focus:ring-volt'
                            }`}
                    >
                        <LanguageFlag code={selectedLanguage?.code ?? value} decorative className="h-5 w-5" />
                        <span className="text-sm font-medium">
                            {getLanguageShortCode(selectedLanguage?.code ?? value)}
                        </span>
                        {!disabled && (
                            <IconChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        )}
                    </button>
                ) : (
                    <button
                        id={controlId}
                        type="button"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between gap-3 bg-background border border-border rounded-lg px-4 py-3 text-left transition-colors ${disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:border-border focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <LanguageFlag
                                code={isEmptySelected ? '' : (selectedLanguage?.code ?? value)}
                                decorative
                                className="h-5 w-5"
                            />
                            {isEmptySelected ? (
                                <span className="text-muted-foreground">{emptyLabel}</span>
                            ) : (
                                <div>
                                    <span className="text-foreground">{selectedLanguage?.name}</span>
                                    {selectedLanguage && !selectedLanguage.name.startsWith(selectedLanguage.nativeName) && (
                                        <span className="text-muted-foreground text-sm ml-2">
                                            ({selectedLanguage.nativeName})
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <IconChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                )}

                {/* Dropdown */}
                {isOpen && (
                    <div
                        data-testid="language-dropdown"
                        style={{ maxHeight }}
                        className={`absolute z-50 ${isCompact ? 'w-64 left-0' : 'w-full'} ${openUpwards ? 'bottom-full mb-2' : 'top-full mt-2'} flex flex-col bg-surface-raised border border-border rounded-lg overflow-hidden`}
                    >
                        {/* Search input */}
                        <div className="p-2 border-b border-border shrink-0">
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search languages..."
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-volt focus:outline-none"
                            />
                        </div>

                        {/* Language list */}
                        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-dark">
                            {allowEmpty && !search && (
                                <button
                                    type="button"
                                    onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
                                    className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-hover transition-colors ${!value ? 'bg-volt/10 text-volt-soft' : 'text-muted-foreground'}`}
                                >
                                    <LanguageFlag code="" decorative className="h-5 w-5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium">{emptyLabel}</div>
                                    </div>
                                    {!value && (
                                        <IconCheck className="w-4 h-4 text-volt shrink-0" />
                                    )}
                                </button>
                            )}
                            {filteredLanguages.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                                    No languages found
                                </div>
                            ) : (
                                filteredLanguages.map((lang) => (
                                    <button
                                        key={lang.code}
                                        type="button"
                                        onClick={() => handleSelect(lang)}
                                        className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-hover transition-colors ${lang.code === value ? 'bg-volt/10 text-volt-soft' : 'text-foreground'
                                            }`}
                                    >
                                        <LanguageFlag code={lang.code} decorative className="h-5 w-5" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{lang.name}</div>
                                            {!lang.name.startsWith(lang.nativeName) && (
                                                <div className="text-xs text-muted-foreground truncate">{lang.nativeName}</div>
                                            )}
                                        </div>
                                        {lang.code === value && (
                                            <IconCheck className="w-4 h-4 text-volt shrink-0" />
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {helperText && !isCompact && (
                <p className="text-xs text-subtle-foreground mt-1">{helperText}</p>
            )}
        </div>
    );
};

export default LanguageSelector;
